import { PluginKey } from '@tiptap/pm/state';
import { Extension } from '@tiptap/react';
import Suggestion, {
  type SuggestionKeyDownProps,
  type SuggestionProps,
} from '@tiptap/suggestion';
import type { RefObject } from 'react';
import type { SpotlightSlashItem } from '../lib/spotlight-slash-items';

import { exitComposerSuggestion } from '../projects-composer-suggestion-dismiss';

const PROJECTS_SKILL_SUGGESTION_NAME = 'projects-skill-suggestion';

export const projectsSkillSuggestionPluginKey = new PluginKey(
  PROJECTS_SKILL_SUGGESTION_NAME
);

export interface ProjectsSkillSuggestionSession {
  readonly query: string;
  readonly items: readonly SpotlightSlashItem[];
  readonly select: (item: SpotlightSlashItem) => void;
  readonly deleteQueryAndRun: (callback: () => void) => void;
  readonly dismiss: () => void;
}

export interface ProjectsSkillSuggestionOptions {
  readonly getItems: (query: string) => readonly SpotlightSlashItem[];
  readonly onSelect: (item: SpotlightSlashItem) => void;
  readonly onSessionChange: (session: ProjectsSkillSuggestionSession | null) => void;
  readonly keyDownRef: RefObject<((props: SuggestionKeyDownProps) => boolean) | null>;
}

export function createProjectsSkillSuggestion(
  options: ProjectsSkillSuggestionOptions
): Extension {
  return Extension.create<ProjectsSkillSuggestionOptions>({
    name: PROJECTS_SKILL_SUGGESTION_NAME,

    addProseMirrorPlugins() {
      return [
        Suggestion<SpotlightSlashItem, SpotlightSlashItem>({
          editor: this.editor,
          pluginKey: projectsSkillSuggestionPluginKey,
          char: '/',
          // Spaces are not valid inside `/` commands — typing one dismisses
          // the palette and the text is treated as plain prose (AGE-1707).
          allowSpaces: false,
          startOfLine: false,

          items: ({ query }) => [...options.getItems(query)],

          command: ({ editor, range, props: item }) =>
            deleteQueryAndRun({ editor, range }, () => options.onSelect(item)),

          render: () => ({
            onStart: (props) => {
              options.onSessionChange(buildSkillSession(props));
            },
            onUpdate: (props) => {
              options.onSessionChange(buildSkillSession(props));
            },
            onKeyDown: (props) => options.keyDownRef.current?.(props) ?? false,
            onExit: () => {
              options.onSessionChange(null);
            },
          }),
        }),
      ];
    },
  });
}

function buildSkillSession(
  props: SuggestionProps<SpotlightSlashItem, SpotlightSlashItem>
): ProjectsSkillSuggestionSession {
  return {
    query: props.query ?? '',
    items: props.items,
    select: (item) => props.command(item),
    deleteQueryAndRun: (callback) => deleteQueryAndRun(props, callback),
    dismiss: () =>
      exitComposerSuggestion(
        props.editor,
        projectsSkillSuggestionPluginKey,
        props.range,
        '/'
      ),
  };
}

function deleteQueryAndRun(
  props: Pick<
    SuggestionProps<SpotlightSlashItem, SpotlightSlashItem>,
    'editor' | 'range'
  >,
  callback: () => void
): void {
  props.editor
    .chain()
    .focus(undefined, { scrollIntoView: false })
    .deleteRange(props.range)
    .run();
  callback();
}
